import React, { createContext, useCallback, useEffect, useRef, useState } from 'react';
import useVideoContext from '../../hooks/useVideoContext/useVideoContext';
import io, { Socket } from 'socket.io-client';
import { ChatMessage, isChatMessage } from './isChatMessage';
const NEW_CHAT_MESSAGE_EVENT = 'newChatMessage'; // Name of the event

export type messageGroup = {
  id: string;
  identity: string;
  ownedByCurrentUser: boolean;
  created: string | Date;
  body: string[];
};

type ChatContextType = {
  isChatWindowOpen: boolean;
  setIsChatWindowOpen: (isChatWindowOpen: boolean) => void;
  sendMessage: (messageBody: string) => void;
  hasUnreadMessages: boolean;
  unreadMessagesCount: number;
  messages: messageGroup[];
};

export const ChatContext = createContext<ChatContextType>(null!);

type ChatProviderProps = {
  children: React.ReactNode;
};

// Messages from the same sender within the same displayed minute are shown as one group
const minuteOf = (created: string | Date) =>
  new Date(created).toLocaleTimeString(navigator.language, { hour: '2-digit', minute: '2-digit' });

export function ChatProvider({ children }: ChatProviderProps) {
  const { room: roomObject, accessToken } = useVideoContext();
  // The room name is what the Twilio access token grants, so it is what meetings-sockets checks
  const room = roomObject?.name;
  const localParticipant = roomObject?.localParticipant;

  const socketRef = useRef<Socket>();
  const isChatWindowOpenRef = useRef(false);
  const [isChatWindowOpen, setIsChatWindowOpen] = useState(false);
  const [messages, setMessages] = useState<messageGroup[]>([]);
  const [hasUnreadMessages, setHasUnreadMessages] = useState(false);
  const [unreadMessagesCount, setUnreadMessagesCount] = useState(0);

  // Updates messages to reflect new ones
  const updateMessages = useCallback(
    (newMessage: ChatMessage) => {
      setMessages((oldMessages) => {
        const lastGroup = oldMessages[oldMessages.length - 1];
        if (
          lastGroup &&
          minuteOf(lastGroup.created) === minuteOf(newMessage.created) &&
          lastGroup.identity === newMessage.identity
        ) {
          const newMessages = [...oldMessages];
          newMessages[newMessages.length - 1] = { ...lastGroup, body: [...lastGroup.body, ...newMessage.body] };
          return newMessages;
        }
        const incomingMessage: messageGroup = {
          id: newMessage.id ?? newMessage.identity,
          identity: newMessage.identity,
          body: newMessage.body,
          created: newMessage.created,
          ownedByCurrentUser: newMessage.identity === localParticipant?.identity,
        };
        return [...oldMessages, incomingMessage];
      });
      if (!isChatWindowOpenRef.current) {
        setUnreadMessagesCount((oldUnreadMessagesCount) => oldUnreadMessagesCount + 1);
      }
    },
    [localParticipant]
  );

  useEffect(() => {
    // The chat socket needs the room and the Twilio access token that proves membership of it
    if (!room || !accessToken) return;

    // Creates a WebSocket connection
    const socket = io(import.meta.env.VITE_SOCKETS_API_URL as string, {
      transports: ['websocket'],
      auth: { room, token: accessToken, application_hash: import.meta.env.VITE_APPLICATION_HASH as string },
    });
    socketRef.current = socket;

    // Listens for incoming messages and drops anything that is not a chat message
    socket.on(NEW_CHAT_MESSAGE_EVENT, (newMessage: unknown) => {
      if (isChatMessage(newMessage)) updateMessages(newMessage);
    });

    // meetings-sockets refuses the handshake with a generic reason when the token is missing,
    // expired or granted for another room. The client does not retry, so leave a trace for support.
    socket.on('connect_error', (error: Error) => {
      console.warn(`Chat socket refused: ${error.message}`);
    });

    // Destroys the socket reference
    // when the connection is closed
    return () => {
      socket.disconnect();
      socketRef.current = undefined;
    };
  }, [room, accessToken, updateMessages]);

  useEffect(() => {
    // If the chat window is closed and there are new messages, set hasUnreadMessages to true
    if (!isChatWindowOpenRef.current && messages.length) {
      setHasUnreadMessages(true);
    }
  }, [messages]);

  useEffect(() => {
    isChatWindowOpenRef.current = isChatWindowOpen;
    if (isChatWindowOpen) {
      setHasUnreadMessages(false);
      setUnreadMessagesCount(0);
    }
  }, [isChatWindowOpen]);

  // Sends a message to the server that
  // forwards it to all users in the same room.
  // The server sets the sender and the timestamp itself; they are still sent here so that
  // a meetings-sockets release that relays the payload as is keeps working during rollout.
  const sendMessage = useCallback(
    (message: string) => {
      socketRef.current?.emit(NEW_CHAT_MESSAGE_EVENT, {
        id: localParticipant?.identity,
        body: [message],
        identity: localParticipant?.identity,
        created: new Date(),
      });
    },
    [localParticipant]
  );

  return (
    <ChatContext.Provider
      value={{ isChatWindowOpen, setIsChatWindowOpen, sendMessage, hasUnreadMessages, unreadMessagesCount, messages }}
    >
      {children}
    </ChatContext.Provider>
  );
}
